import React, { ChangeEvent, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Content } from '../../../../_myb/layout/components/content';
import { fetchContact, fetchFieldConfig, lookupGSTIN, sendPortalInvite, upSertContact, upSertContactWithImage } from '../../../api';
import { CategoryPicker } from './CategoryPicker';
import { Address, AttrType, Contact, ContactType, CustomField, FieldConfig } from '../../../types';
import { toast } from 'react-toastify';
import { scrollToFirstError } from '../../utils/MiscFunctions';
import DynamicCheckboxGroup from '../../widgets/components/DynamicCheckBoxGroup';
import PhoneNumberInput from '../../widgets/components/PhoneNumberInput';
import EmailInput from '../../widgets/components/EmailInput';
import { GenericPicker } from './AttributePicker';
import PictureUploadBox from '../../widgets/components/PictureUploadBox';
import { CurrencyPicker } from '../../settings/Currency';
import { PaymentTermsPicker } from '../../settings/PaymentTerms';
import { Button } from 'react-bootstrap';
import { ToolbarContext, defaultToolbarState } from '../../../../_myb/layout/components/toolbar/Toolbar';
import NumberInput from '../../widgets/components/NumberInput';
import SliderSwitch from '../../widgets/components/SliderSwitch';
import Flatpickr from '../../widgets/components/FlatpickrSafe';
import LabelWithTooltip from '../../widgets/components/CustomLabel';
import { CustomPrompt } from '../../widgets/components/ModalPrompts';
import FormLoader from '../../widgets/components/Form Loader';
import { useAppContext } from '../../apps/AppContext';
import { EasyPicker } from '../../widgets/components/SimplePicker';
import { GSTTreatments } from '../../settings/TaxSettings';
import ButtonWithLoader from '../../widgets/components/ButtonWithLoader';
import { brandSettings } from '../../settings/BrandSettings';
import CustomModal from '../../widgets/components/CustomModal';
import { AddFieldConfigurator, EditFieldConfigurator } from '../../widgets/components/FieldConfigs';
import { trackEvent, AnalyticsEvent } from '../../apps/analytics';

interface ContactFormProps {
  contactType?: ContactType;
  onClose?: () => void;
  onSave?: (contact: Contact) => void;
  quickCreate?: boolean;
  contactName?: string;
  modalState?: boolean
}

export interface FieldError {
  id: string;
  message: string;
}


export function ContactForm({ contactType, onClose, onSave, quickCreate, contactName, modalState }: ContactFormProps) {
  const { contactId } = useParams<{ contactId: string }>();
  const navigate = useNavigate();
  const { bizMaster } = useAppContext();
  const isCIN = (bizMaster?.bizDetails?.country === 'IN')
  const [isFormDirty, setIsFormDirty] = useState<boolean>(false);
  const suggestContactType = contactType ? [contactType] : []
  const [contact, setContact] = useState<Contact>(new Contact({ contactTyp: suggestContactType }));
  const { updateToolbar } = useContext(ToolbarContext);
  const [file, setFile] = useState<File>()
  const [loading, setLoading] = useState<boolean>(false);
  const [contactImages, setContactImages] = useState<string[]>([]);
  const contactNameRef = useRef<HTMLInputElement>(null);
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [fieldConfig, setFieldConfigs] = useState<FieldConfig[]>([])
  const [showAddFieldModal, setShowAddFieldModal] = useState(false)
  const [showEditFieldModal, setShowEditFieldModal] = useState(false)
  // MBAI-121: lets Quick Create expand in place to the full contact form, instead of only offering a handful of fields
  const [isExpanded, setIsExpanded] = useState(false);

  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editingAddressIndex, setEditingAddressIndex] = useState<number | null>(null);
  const [currentAddress, setCurrentAddress] = useState<Address>({ addressName: '', addressDetail: '' });
  const [addressType, setAddressType] = useState<'shipping' | 'billing'>('shipping');
  // Inline error: a toast can render behind the modal, so the required-field error is also shown in the modal
  const [addressDetailError, setAddressDetailError] = useState<string>('');
  // MBAI-301/448: GSTIN inline validation state — warning only, never blocks submit
  const [gstinWarning, setGstinWarning] = useState<string>('');
  const [gstinFetching, setGstinFetching] = useState<boolean>(false);
  const [sendingInvite, setSendingInvite] = useState<boolean>(false);
  const GSTIN_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;




  // Effect to update toolbar and fetch contact data
  useEffect(() => {
    if (contactNameRef.current) {
      const timer = setTimeout(() => {
        contactNameRef.current?.focus();
      }, 500); // Delay the focus slightly to ensure the modal is fully rendered

      return () => clearTimeout(timer); // Cleanup the timer on unmount
    }
  }, [modalState]);

  useEffect(() => {
    updateToolbar({
      ...defaultToolbarState,
      alwaysDisplay: true,
    });

    const getContact = async () => {
      try {
        if (contactId) {
          setLoading(true);
          const { contact, files } = await fetchContact(contactId);

          // Set the contact details
          setContact(contact);
          if (contact.customFields) {
            setCustomFields(contact.customFields);
          }
          // Process image files
          if (files && Array.isArray(files)) {
            const imageUrls = files.map(contactImgFile => {
              try {
                if (contactImgFile instanceof Blob && contactImgFile.type.startsWith('image/')) {
                  // If it's a Blob or File and it's an image, create an object URL
                  return URL.createObjectURL(contactImgFile);
                } else {
                  console.error('Invalid file type for createObjectURL or unsupported content:', contactImgFile);
                  return null;
                }
              } catch (err) {
                console.error('Error creating object URL for image:', err);
                return null;
              }
            }).filter((url): url is string => url !== null); // Filter out any null URLs and ensure TypeScript treats the array as string[]

            setContactImages(imageUrls); // Set the image URLs
          }

          setLoading(false); // Set loading to false after all processing is complete
        }
      } catch (error) {
        console.error('Error fetching contact:', error);
        toast.error('Failed to load contact data.'.toTranslatedDisplay());
        setLoading(false); // Ensure loading is stopped in case of error
      }
    };


    getContact();
    
  }, [contactId]);

  useEffect(()=>{
    getFieldConfig()
  },[showAddFieldModal,showEditFieldModal])

  const getFieldConfig = async () => {
    try {
      const fieldConfigResult = await fetchFieldConfig('Contact')
      setFieldConfigs(fieldConfigResult)
    } catch (error) {
      console.error('Error fetching Field Configs', error);
    }
  }

  // Handle input changes and mark form as dirty
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;

    setContact((prevContact) => ({
      ...prevContact,
      [name]: value,
    }));
    setIsFormDirty(true);
  };

  useEffect(() => {
    // Check if contactName contains only phone number characters
    const phoneNumberRegex = /^[0-9+\-()\s]+$/; // Adjust the regex for phone numbers as needed
    if (contactName) {
      setContact((prevItem: any) => ({
        ...prevItem,
        contactName: phoneNumberRegex.test(contactName) ? prevItem.contactName : contactName,
        workPhone: phoneNumberRegex.test(contactName) ? contactName : prevItem.workPhone,
      }));
    }

  }, [contactName]);

  const [errors, setErrors] = useState<{ [key: string]: FieldError }>({});
  const newErrors: { [key: string]: FieldError } = {};
  // Handle exit with unsaved changes confirmation
  const [isPromptVisible, setPromptVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const handleExit = () => {
    if (isFormDirty) {
      setPromptVisible(true);
    } else {
      if (onClose) {
        onClose()
      } else {
        navigate(-1);
      }


    }
  };

  const handleConfirmExit = () => {
    setPromptVisible(false);
    if (onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  };

  const handleClosePrompt = () => {
    setPromptVisible(false);
  };

  const handleEmailError = (hasError: boolean) => {
    // Validate contact name
    if (hasError) {
      newErrors.emailTxt = { id: 'emailTxt', message: 'Invalid email!'.toTranslatedDisplay() };
      setErrors(newErrors);
    }
  };

  function handleAddFieldModalToggle(): void {
    setShowAddFieldModal(!showAddFieldModal)
  }

  function handleAddFieldModalClose(): void {
    setShowAddFieldModal(false)
  }

  function handleEditFieldModalClose(): void {
    setShowEditFieldModal(false)
  }

  function handleEditFieldModalToggle(): void {
    setShowEditFieldModal(!showEditFieldModal)
  }

  function handleCustomFields(event: ChangeEvent<HTMLInputElement>): void {
    const { name, value } = event.target;

    setContact(prevContact => {
      const customFields = prevContact.customFields || [];

      // Update the customFields array by finding the matching field by fieldName
      const updatedFields = customFields.map(field =>
        field.fieldName === name ? { ...field, fieldValue: value } : field
      );

      // If the field does not exist, add a new one
      if (!customFields.some(field => field.fieldName === name)) {
        updatedFields.push({ fieldName: name, fieldValue: value });
      }

      return {
        ...prevContact,
        customFields: updatedFields,
      };
    });
  }

  // MBAI-301/448: validate GSTIN format on change and fetch business details on demand.
  // Mismatch is surfaced as a non-blocking warning — it must not prevent the form from saving.
  const handleGSTINChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.toUpperCase();
    handleInputChange({ ...e, target: { ...e.target, name: 'taxIdNo', value: val } } as any);
    if (val.length === 0) {
      setGstinWarning('');
    } else if (!GSTIN_PATTERN.test(val)) {
      setGstinWarning('This doesn\'t look like a standard GSTIN format (e.g. 29ABCDE1234F1Z5) — please double-check.');
    } else {
      setGstinWarning('');
    }
  };

  const handleSendPortalInvite = async () => {
    if (!contactId) return;
    setSendingInvite(true);
    try {
      await sendPortalInvite(contactId);
      toast.success('Portal invite sent.'.toTranslatedDisplay());
    } catch (error: any) {
      toast.error(error.message || 'Failed to send portal invite.'.toTranslatedDisplay());
    } finally {
      setSendingInvite(false);
    }
  };

  const handleFetchGSTIN = async () => {
    const gstin = (contact.taxIdNo || '').toUpperCase();
    if (!GSTIN_PATTERN.test(gstin)) {
      setGstinWarning('Enter a valid GSTIN before fetching'.toTranslatedDisplay());
      return;
    }
    setGstinFetching(true);
    try {
      const result = await lookupGSTIN(gstin);
      if (result?.legalName || result?.tradeName) {
        setContact((prev: any) => ({
          ...prev,
          contactName: prev.contactName || result.legalName || result.tradeName,
          companyName: result.tradeName || result.legalName,
          isBusiness: true,
        }));
        toast.success('GSTIN details fetched successfully!'.toTranslatedDisplay());
      } else {
        toast.info('GSTIN is valid — no additional details returned.'.toTranslatedDisplay());
      }
    } catch {
      toast.error('Could not fetch GSTIN details — check your API key configuration.'.toTranslatedDisplay());
    } finally {
      setGstinFetching(false);
    }
  };

  // Handle form submission with validation and error handling
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true)
    // Validate contact name
    if ((!contact.contactName || contact.contactName.trim() === '') && (!contact.workPhone || contact.workPhone.trim() === '') && !loading) {
      newErrors.contactName = { id: 'contactName', message: 'Contact Name or Phone cannot be empty'.toTranslatedDisplay() };
      setErrors((prevErrors) => ({
        ...prevErrors,
        ...newErrors
      }));
      setIsLoading(false);
      toast.error('Contact Name or Phone cannot be empty'.toTranslatedDisplay());
      // MBAI-28: scroll to first invalid field
      scrollToFirstError('contactName');
      return;
    }
    // MBAI-DEVISSUES #16: block save on a malformed email (EmailInput only flags it inline)
    if (contact.emailTxt && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.emailTxt.trim())) {
      setIsLoading(false);
      toast.error('Invalid email!'.toTranslatedDisplay());
      scrollToFirstError('emailTxt');
      return;
    }
    try {
      const updatedContact = await upSertContactWithImage(contact, file);
      if (!contactId) {
        trackEvent(AnalyticsEvent.ContactCreated);
      }
      setIsFormDirty(false);
      toast.success('Contact saved successfully!'.toTranslatedDisplay());
      if (onSave) {
        setContact(new Contact({ contactTyp: suggestContactType }))
        onSave(contact)
      } else {
        navigate('/contacts/view-contacts');
      }

      setIsLoading(false)
    } catch (error: any) {
      console.error('Error upserting contact:', error);  // Log the full error object
      setIsLoading(false)
      const errorMessage = error.message || 'Failed to save contact due to an unknown error.';
      toast.error(errorMessage);  // Show error message
    }

  };

  const handleImageUpload = (image: File | undefined) => {
    if (image) {
      // Handle file upload
      setFile(image);
    }
    // Perform the upload or other processing with the image file
  };

  // const [selection, setSelection] = useState<string[]>([]);
  const handleSelectionChange = (newSelection: string[]) => {
    // setSelection(newSelection);

    // Assuming ContactType is an enum or a union of string literals
    const updatedContactTyp: ContactType[] = newSelection.map((selection) => {
      return selection as ContactType;  // Cast the string to ContactType
    });

    setContact((prevContact) => ({
      ...prevContact,
      contactTyp: updatedContactTyp,
      ...(updatedContactTyp.length === 0 && {
        openingBalance: undefined,
        openingBalanceDate: undefined,
      }),
    }));

    setIsFormDirty(true);
  };

  const handleSwitchChange = (name: string, checked: boolean) => {
    setIsFormDirty(true);
  };

  if (loading) {
    return <FormLoader />; // Show the content loader while loading
  }

  const handleAddressModalToggle = (type: 'shipping' | 'billing') => {
    setAddressType(type);
    setCurrentAddress({ addressName: '', addressDetail: '' });
    setEditingAddressIndex(null);
    setShowAddressModal(true);
  };

  const handleAddressModalClose = () => {
    setShowAddressModal(false);
    setCurrentAddress({ addressName: '', addressDetail: '' });
    setEditingAddressIndex(null);
    setAddressDetailError('');
  };

  const handleAddressSave = () => {
    if (!currentAddress.addressDetail.trim()) {
      setAddressDetailError('Address detail is required'.toTranslatedDisplay());
      toast.error('Address detail is required'.toTranslatedDisplay());
      return;
    }

    setContact((prevContact) => {
      const addresses = prevContact.addresses || [];
      const updatedAddress = {
        ...currentAddress,
        addressType: addressType // Store the type with each address
      };

      if (editingAddressIndex !== null) {
        // Edit existing address
        const updatedAddresses = [...addresses];
        updatedAddresses[editingAddressIndex] = updatedAddress;
        return { ...prevContact, addresses: updatedAddresses };
      } else {
        // Add new address
        return { ...prevContact, addresses: [...addresses, updatedAddress] };
      }
    });

    setIsFormDirty(true);
    handleAddressModalClose();
  };

  const handleAddressEdit = (index: number) => {
    const address = contact.addresses?.[index];
    if (address) {
      setCurrentAddress({ addressName: address.addressName, addressDetail: address.addressDetail });
      setAddressType((address as any).addressType || 'shipping');
      setEditingAddressIndex(index);
      setShowAddressModal(true);
    }
  };

  const handleAddressDelete = (index: number) => {
    setContact((prevContact) => {
      const addresses = prevContact.addresses || [];
      return { ...prevContact, addresses: addresses.filter((_, i) => i !== index) };
    });
    setIsFormDirty(true);
  };

  const handleAddressInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setCurrentAddress((prev) => ({ ...prev, [name]: value }));
  };

  if (quickCreate && !isExpanded) {
    return (
      <div className='col'>
        <div className='d-flex justify-content-end mb-2'>
          <button
            type='button'
            className='btn btn-sm btn-light-primary'
            onClick={() => setIsExpanded(true)}
            title={'Show the full contact form'.toTranslatedDisplay()}
          >
            <i className='bi bi-arrows-angle-expand me-1'></i>
            {'Expand'.toTranslatedDisplay()}
          </button>
        </div>
        <div className='row mb-3'>
          <div className='col-md-4 mb-3'>
            <label className='form-label'>{'Contact Name'.toTranslatedDisplay()}</label>
            <input
              type='text'
              className={`form-control ${errors.contactName ? 'is-invalid' : ''}`}
              placeholder={'Contact Name'.toTranslatedDisplay()}
              name='contactName'
              value={contact.contactName || ''}
              onChange={handleInputChange}
              tabIndex={1}
              ref={contactNameRef}
            />
            {errors.contactName && <div className="invalid-feedback">{errors.contactName.message}</div>}
          </div>
          <div className='col-md-4 mb-3'>
            <PhoneNumberInput
              label={'Phone #'.toTranslatedDisplay()}
              name={'workPhone'}
              value={contact.workPhone || ''}
              onChange={handleInputChange}
              tabIndex={1}
            />
          </div>
          <div className='col-md-4 mb-3'>
            <EmailInput
              label={'Email'.toTranslatedDisplay()}
              name="emailTxt"
              value={contact.emailTxt || ''}
              placeholder={'Enter email address'.toTranslatedDisplay()}
              onChange={handleInputChange}
              onError={handleEmailError} // Pass the error handler
              tabIndex={1}
            />
          </div>
        </div>
        {/* MBAI-121: sprint-review answer (2026-09-04) expanded Quick Create to the
            5-6 most commonly used fields — name/phone/email above, tax ID/address here. */}
        <div className='row mb-3'>
          <div className='col-md-6 mb-3'>
            <label className='form-label'>{(isCIN ? 'GST' : 'VAT').toTranslatedDisplay()}</label>
            <input
              type='text'
              className='form-control'
              placeholder={(isCIN ? 'GSTIN (e.g. 29ABCDE1234F1Z5)' : 'VAT #').toTranslatedDisplay()}
              name='taxIdNo'
              value={contact.taxIdNo || ''}
              onChange={isCIN ? handleGSTINChange : handleInputChange}
              maxLength={isCIN ? 15 : undefined}
              style={{ textTransform: isCIN ? 'uppercase' : undefined }}
              tabIndex={2}
            />
          </div>
          <div className='col-md-6 mb-3'>
            <label className='form-label'>{'Address'.toTranslatedDisplay()}</label>
            <textarea
              className='form-control'
              placeholder={'Default Address'.toTranslatedDisplay()}
              name='billingAddress'
              value={contact.billingAddress || ''}
              onChange={handleInputChange}
              tabIndex={2}
            />
          </div>
        </div>
        <div className='d-flex'>
          <ButtonWithLoader shortcutTip='Ctrl+M / ⌘+M' isLoading={isLoading} shortcutKeys={
            ['ctrl+m', 'meta+m']
          } onClick={handleSubmit} >
            {'Save'.toTranslatedDisplay()}
          </ButtonWithLoader>
          <button type='button' className='btn btn-secondary ms-2' onClick={handleExit} tabIndex={3}>{'Cancel'.toTranslatedDisplay()}</button>
          <div className='text-end w-100 mt-3'>
            <span className='ms-3 badge badge-secondary p-2'>{'Press Esc to use Contact without saving'.toTranslatedDisplay()}</span>
          </div>

        </div>
      </div>
    )
  }

  // MBAI-121: Quick Create's expand button renders this same full form inline inside the modal,
  // so the page-level <Content> container (page padding/max-width meant for a routed page) is skipped in that case.
  const FormWrapper = quickCreate ? React.Fragment : Content;

  return (
    <FormWrapper>
      <div className='card mb-5 mb-xl-10' id='kt_profile_details_view'>
        <div className='card-header cursor-pointer'>
          <div className='card-title m-0'>
            <h3 className='fw-bolder m-0'>{'Contact Details'.toTranslatedDisplay()}</h3>
          </div>
          <div className='card-toolbar'>
            <ButtonWithLoader shortcutTip='Ctrl+S / ⌘+S' isLoading={isLoading} onClick={handleSubmit} >
              {'Save'.toTranslatedDisplay()}
            </ButtonWithLoader>
            <button type='button' className='btn btn-secondary ms-2' onClick={handleExit} tabIndex={3}>{'Cancel'.toTranslatedDisplay()}</button>
          </div>
        </div>

        <div className='card-body p-9'>
          <div className='row mb-3'>
            <DynamicCheckboxGroup options={[ContactType.Customer, ContactType.Vendor, ContactType.Employee]} selectedValues={contact.contactTyp} onSelectionChange={handleSelectionChange} />
            <div className='col-md-3'>
              <div className='mb-3'>
                <label className='form-label'>{'Contact Name'.toTranslatedDisplay()}</label>
                <input
                  type='text'
                  className={`form-control ${errors.contactName ? 'is-invalid' : ''}`}
                  placeholder={'Contact Name'.toTranslatedDisplay()}
                  name='contactName'
                  value={contact.contactName || ''}
                  onChange={handleInputChange}
                  tabIndex={1}
                  ref={contactNameRef}
                />
                {errors.contactName && <div className="invalid-feedback">{errors.contactName.message}</div>}
              </div>
              <label className='form-label'>{'Contact Type'.toTranslatedDisplay()}</label>
              <div className='d-flex align-items-center mt-1'>
                <SliderSwitch defaultChecked={contact.isBusiness}
                  onChange={(name, isBusiness) => {
                    setContact((prevItem: any) => ({ ...prevItem, isBusiness: isBusiness }))
                  }}
                  name='isBusiness' />
                <p className='mb-0 ms-2'>{(contact.isBusiness) ? 'Business'.toTranslatedDisplay() : 'Individual'.toTranslatedDisplay()}</p>
              </div>
              {contact.isBusiness &&
                <div className='my-3'>
                  <label className='form-label'>{'Business Name'.toTranslatedDisplay()}</label>
                  <input
                    type='text'
                    className={`form-control ${errors.contactName ? 'is-invalid' : ''}`}
                    placeholder={'Business Name'.toTranslatedDisplay()}
                    name='companyName'
                    value={contact.companyName || ''}
                    onChange={handleInputChange}
                  />
                </div>
              }
            </div>
            <div className='col-md-3'>
              <div className='mb-3'>
                <PhoneNumberInput
                  label={'Phone #'.toTranslatedDisplay()}
                  name={'workPhone'}
                  value={contact.workPhone || ''}
                  onChange={handleInputChange}
                  tabIndex={1}
                />
              </div>
              <label className='form-label'>{'Contact Category'.toTranslatedDisplay()}</label>
              <CategoryPicker
                onCategoryChange={(categoryId) => setContact((prevItem: any) => ({ ...prevItem, contactCategory: categoryId }))}
                categoryType={AttrType.ContactCategory}
                selectedCategory={contact.contactCategory}
                tabIndex={2}
              />
            </div>
            <div className='col-md-3'>
              <div className='mb-3'>
                <EmailInput
                  label={'Email'.toTranslatedDisplay()}
                  name="emailTxt"
                  value={contact.emailTxt || ''}
                  placeholder={'Enter email address'.toTranslatedDisplay()}
                  onChange={handleInputChange}
                  onError={handleEmailError} // Pass the error handler
                  tabIndex={1}
                />
              </div>
              {brandSettings.hideContactGroupAttr !== true &&
                <GenericPicker
                  categoryType={AttrType.ContactGroup}
                  selectedAttribute={contact.contactGroup}
                  onAttributeChange={(attributeId) => setContact((prevItem: any) => ({ ...prevItem, contactGroup: attributeId }))}
                  showColor={false}
                  label={'Contact Group'.toTranslatedDisplay()}
                  tabIndex={2}
                />
              }
              {brandSettings.showPropertyAttr === true &&
                <GenericPicker
                  categoryType={AttrType.Property}
                  selectedAttribute={contact.property}
                  onAttributeChange={(attributeId) => setContact((prevItem: any) => ({ ...prevItem, property: attributeId }))}
                  showColor={false}
                  label={'Property'.toTranslatedDisplay()}
                  tabIndex={2}
                />
              }

            </div>
            <div className='col-md-3'>
              <PictureUploadBox onUpload={handleImageUpload} selectedImage={contactImages[0]} />
            </div>
          </div>

          <ul className='nav nav-tabs nav-line-tabs mb-5 fs-6 fw-bolder '>
            <li className='nav-item'>
              <a
                className='nav-link active text-active-primary'
                data-bs-toggle='tab'
                href='#kt_sales_purchase_settings'
              >
                {'Payment Details'.toTranslatedDisplay()}
              </a>
            </li>
            <li className='nav-item'>
              <a className='nav-link text-active-primary' data-bs-toggle='tab' href='#kt_tab_pane_2'>
                {'Address Details'.toTranslatedDisplay()}
              </a>
            </li>
            <li className='nav-item'>
              <a className='nav-link text-active-primary' data-bs-toggle='tab' href='#kt_tab_pane_3'>
                {'Opening Balance'.toTranslatedDisplay()}
              </a>
            </li>
            <li className='nav-item'>
              <a className='nav-link text-active-primary' data-bs-toggle='tab' href='#kt_tab_pane_4'>
                {'Custom Fields'.toTranslatedDisplay()}
              </a>
            </li>
          </ul>

          <div className='tab-content' id='myTabContent'>
            <div
              className='tab-pane fade active show'
              id='kt_sales_purchase_settings'
              role='tabpanel'
            >
              {/* Row 1: Currency / Payment Terms / Credit Limit / Portal Access */}
              <div className='row mb-3'>
                <div className='col-md-3'>
                  <div className='App'>
                    <CurrencyPicker selectedCurrency={contact.currId}
                      onSelectionChange={(currId) => setContact((prevItem: any) => ({ ...prevItem, currId: currId }))}
                      label={'Currency'.toTranslatedDisplay()} />
                  </div>
                </div>
                <div className='col-md-3'>
                  <LabelWithTooltip
                    labelText="Payment Terms"
                    tooltipText="Payment Terms define the conditions under which a sale is completed, including when payment is due and any potential early payment discounts or late payment penalties."
                  />
                  <PaymentTermsPicker
                    label={undefined}
                    selectedValue={contact.paymentTerms}
                    onSelectionChange={(paymentTerm) => setContact((prevItem: any) => ({ ...prevItem, paymentTerms: paymentTerm }))}
                  />
                </div>
                <div className='col-md-3'>
                  <NumberInput
                    label={'Credit Limit'.toTranslatedDisplay()}
                    tooltip={'Maximum outstanding balance allowed for this contact. Leave blank for no limit.'}
                    name='creditLimit'
                    value={contact.creditLimit}
                    onChange={handleInputChange}
                    showCurrency
                  />
                </div>
                {contact.contactTyp.includes(ContactType.Customer) &&
                  <div className='col-md-3'>
                    {/* spacer to align toggle with the labeled inputs/selects in this row */}
                    <label className='form-label d-block'>&nbsp;</label>
                    <div className='d-flex align-items-center'>
                      <SliderSwitch
                        name='portalAccess'
                        defaultChecked={contact.portalAccess}
                        onChange={(_name, checked) => {
                          setContact((prev: any) => ({ ...prev, portalAccess: checked }));
                          setIsFormDirty(true);
                        }}
                      />
                      <LabelWithTooltip
                        className='mb-0 ms-2 form-label'
                        labelText="Portal Access"
                        tooltipText="Let this customer log in to the Customer Portal to view invoices, pay online, and check their statement."
                      />
                    </div>
                  </div>
                }
              </div>

              {/* Row 2: Tax Preference / GST Treatment / No Late Fee / Portal Login Email */}
              <div className='row mb-3'>
                <div className='col-md-3'>
                  <LabelWithTooltip
                    className='form-label'
                    labelText="Tax Preference"
                    tooltipText="Mark this contact as tax exempt to skip tax calculation on transactions with them, regardless of the item's tax settings."
                  />
                  <div className='d-flex align-items-center mt-3'>
                    <SliderSwitch defaultChecked={contact.taxExempt}
                      onChange={(name, taxPreference) => {
                        setContact((prevItem: any) => ({ ...prevItem, taxExempt: taxPreference }))
                      }}
                      name='taxExempt' />
                    <p className='mb-0 ms-2'>{(contact.taxExempt) ? 'Exempt'.toTranslatedDisplay() : 'Non-Exempt'.toTranslatedDisplay()}</p>
                  </div>
                </div>
                <div className='col-md-3'>
                  {isCIN &&
                    <EasyPicker label={'GST Treatment'.toTranslatedDisplay()} name='inGSTTreatment' options={GSTTreatments} value={contact.inGSTTreatment}
                      onSelectionChange=
                      {handleInputChange} />
                  }
                </div>
                <div className='col-md-3'>
                  {/* spacer to align toggle with the labeled select in this row */}
                  <label className='form-label d-block'>&nbsp;</label>
                  <div className='d-flex align-items-center'>
                    <SliderSwitch
                      name='noLateFee'
                      defaultChecked={contact.noLateFee}
                      onChange={(_name, checked) => {
                        setContact((prev: any) => ({ ...prev, noLateFee: checked }));
                      }}
                    />
                    <label className='mb-0 ms-2 form-label'>{'No Late Fee'.toTranslatedDisplay()}</label>
                  </div>
                </div>
                {contact.contactTyp.includes(ContactType.Customer) && contact.portalAccess &&
                  <div className='col-md-3'>
                    <EmailInput
                      label={'Portal Login Email'.toTranslatedDisplay()}
                      name="portalEmail"
                      value={contact.portalEmail || ''}
                      placeholder={contact.emailTxt || 'Defaults to contact email'.toTranslatedDisplay()}
                      onChange={handleInputChange}
                      onError={() => {}}
                      tabIndex={1}
                    />
                  </div>
                }
              </div>

              {/* Row 3: GST/VAT / (empty) / PAN / Send Portal Invite */}
              <div className='row mb-7'>
                <div className='col-md-3'>
                  <LabelWithTooltip
                    className='form-label'
                    labelText={isCIN ? 'GST' : 'VAT'}
                    tooltipText={isCIN
                      ? 'GST Identification Number (GSTIN) — a 15-character code based on PAN, used to identify this contact for GST purposes.'
                      : 'VAT registration number for this contact.'}
                  />
                  {/* MBAI-301/448: GSTIN inline validator (warning only) + fetch button */}
                  <div className='input-group'>
                    <input
                      type='text'
                      className={`form-control ${isCIN && gstinWarning ? 'border-warning' : ''}`}
                      placeholder={(isCIN ? 'GSTIN (e.g. 29ABCDE1234F1Z5)' : 'VAT #').toTranslatedDisplay()}
                      name='taxIdNo'
                      value={contact.taxIdNo || ''}
                      onChange={isCIN ? handleGSTINChange : handleInputChange}
                      maxLength={isCIN ? 15 : undefined}
                      style={{ textTransform: isCIN ? 'uppercase' : undefined }}
                    />
                    {isCIN && (
                      <button
                        type='button'
                        className='btn btn-outline-secondary btn-sm'
                        onClick={handleFetchGSTIN}
                        disabled={gstinFetching || !contact.taxIdNo}
                        title={'Fetch business details from GSTIN'.toTranslatedDisplay()}
                      >
                        {gstinFetching ? <span className='spinner-border spinner-border-sm' /> : <i className='las la-search' />}
                      </button>
                    )}
                  </div>
                  {isCIN && gstinWarning && (
                    <div className='text-warning small mt-1 d-flex align-items-center'>
                      <i className='las la-exclamation-triangle me-1' />
                      {gstinWarning.toTranslatedDisplay()}
                    </div>
                  )}
                </div>
                <div className='col-md-3' />
                <div className='col-md-3'>
                  {isCIN && <>
                    <label className='form-label'>{'PAN'.toTranslatedDisplay()}</label>
                    <input
                      type='text'
                      className='form-control'
                      placeholder={'PAN No'.toTranslatedDisplay()}
                      name='panNo'
                      value={contact.panNo || ''}
                      onChange={handleInputChange}
                    />
                  </>}
                </div>
                {contact.contactTyp.includes(ContactType.Customer) && contact.portalAccess &&
                  <div className='col-md-3'>
                    <button
                      type='button'
                      className='btn btn-sm btn-light-primary'
                      onClick={handleSendPortalInvite}
                      disabled={!contactId || sendingInvite}
                      title={!contactId ? 'Save this contact first'.toTranslatedDisplay() : undefined}
                    >
                      {sendingInvite ? <span className='spinner-border spinner-border-sm me-1' /> : <i className='bi bi-send me-1'></i>}
                      {'Send Portal Invite'.toTranslatedDisplay()}
                    </button>
                  </div>
                }
              </div>

            </div>
            <div className='tab-pane fade' id='kt_tab_pane_2' role='tabpanel'>
              <div className='row mb-7'>
                <div className='col-md-4'>
                  <div className='mb-3'>
                    <label className='form-label'>{'Billing Address'.toTranslatedDisplay()}</label>
                    <textarea
                      className='form-control'
                      placeholder={'Default Address'.toTranslatedDisplay()}
                      name='billingAddress'
                      value={contact.billingAddress || ''}
                      onChange={handleInputChange}
                    />
                    
                  </div>
                  <div className='mb-3'>
                      <label className='form-label d-flex justify-content-between align-items-center'>
                        {'Additional Billing Addresses'.toTranslatedDisplay()}
                        <button
                          type='button'
                          className='btn btn-sm btn-light-primary'
                          onClick={() => handleAddressModalToggle('billing')}
                        >
                          <i className='bi bi-plus-circle me-1'></i>
                          {'Add Address'.toTranslatedDisplay()}
                        </button>
                      </label>
                      {contact.addresses && contact.addresses.filter((addr: any) => addr.addressType === 'billing').length > 0 && (
                        <div className='d-flex flex-wrap gap-2 mt-2'>
                          {contact.addresses.map((address: any, index) =>
                            address.addressType === 'billing' && (
                              <span key={index} className='badge badge-light-info p-3 d-flex text-start align-items-center'>
                                <span className='me-2'>
                                  <strong>{address.addressName || `Address ${index + 1}`}</strong>
                                  <br />
                                  <small className='text-muted'>{address.addressDetail?.substring(0, 30)}...</small>
                                </span>
                                <i
                                  className='bi bi-pencil-square me-2 cursor-pointer text-primary'
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => handleAddressEdit(index)}
                                ></i>
                                <i
                                  className='bi bi-x-circle cursor-pointer text-danger'
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => handleAddressDelete(index)}
                                ></i>
                              </span>
                            )
                          )}
                        </div>
                      )}
                    </div>
                </div>
                <div className='col-md-4'>
                  <div className='mb-3'>
                    <label className='form-label d-flex justify-content-between align-items-center'>
                      {'Shipping Address'.toTranslatedDisplay()}
                      <a
                        className=""
                        role="button"
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          // Implement copy from billing address
                          setContact((prevContact) => ({
                            ...prevContact,
                            shippingAddress: prevContact.billingAddress,
                          }));
                          setIsFormDirty(true);
                        }}
                      >
                        {'Copy Billing'.toTranslatedDisplay()}
                      </a>
                    </label>
                    <textarea
                      className='form-control'
                      placeholder={'Default Address'.toTranslatedDisplay()}
                      name='shippingAddress'
                      value={contact.shippingAddress || ''}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className='mb-3'>
                    <label className='form-label d-flex justify-content-between align-items-center'>
                      {'Additional Shipping Addresses'.toTranslatedDisplay()}
                      <button
                        type='button'
                        className='btn btn-sm btn-light-primary'
                        onClick={() => handleAddressModalToggle('shipping')}
                      >
                        <i className='bi bi-plus-circle me-1'></i>
                        {'Add Address'.toTranslatedDisplay()}
                      </button>
                    </label>
                    {contact.addresses && contact.addresses.filter((addr: any) => addr.addressType === 'shipping').length > 0 && (
                      <div className='d-flex flex-wrap gap-2 mt-2'>
                        {contact.addresses.map((address: any, index) =>
                          address.addressType === 'shipping' && (
                            <span key={index} className='badge badge-light-success p-3 d-flex align-items-center'>
                              <span className='me-2'>
                                <strong>{address.addressName || `Address ${index + 1}`}</strong>
                                <br />
                                <small className='text-muted'>{address.addressDetail?.substring(0, 30)}...</small>
                              </span>
                              <i
                                className='bi bi-pencil-square me-2 cursor-pointer text-primary'
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleAddressEdit(index)}
                              ></i>
                              <i
                                className='bi bi-x-circle cursor-pointer text-danger'
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleAddressDelete(index)}
                              ></i>
                            </span>
                          )
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className='tab-pane fade' id='kt_tab_pane_3' role='tabpanel'>
              <div className='row mb-7'>
                <div className='col-md-3'>
                  {contact.contactTyp.includes(ContactType.Customer) &&
                    <div className='mb-3'>
                      <NumberInput
                        label={'Opening Balance Receivable(Optional)'.toTranslatedDisplay()}
                        tooltip={'Amount this customer already owed you before you started using myBooks, as of the date set below.'}
                        name='receivableAmt'
                        value={contact.receivableAmt}
                        onChange={handleInputChange}
                        showCurrency
                      />
                    </div>
                  }

                  {contact.contactTyp.includes(ContactType.Vendor) &&
                    <div className='mb-3'>
                      <NumberInput
                        label={'Opening Balance Payable(Optional)'.toTranslatedDisplay()}
                        tooltip={'Amount you already owed this vendor before you started using myBooks, as of the date set below.'}
                        name='payableAmt'
                        value={contact.payableAmt}
                        onChange={handleInputChange}
                        showCurrency
                      />
                    </div>
                  }

                </div>
                <div className='col-md-3'>
                  {contact.contactTyp.includes(ContactType.Customer) &&
                    <div className='mb-3'>
                      <LabelWithTooltip
                        labelText="Opening Receivable Date"
                        tooltipText="This is the date for the opening balance"
                      />
                      <Flatpickr
                        value={contact.receivableDate}
                        onChange={([date]) => setContact((prevItem: any) => ({ ...prevItem, receivableDate: date }))}
                        className='form-control'
                        placeholder={'Opening Balance Date'.toTranslatedDisplay()}
                      />
                    </div>
                  }
                  {contact.contactTyp.includes(ContactType.Vendor) &&
                    <div className='mb-3'>
                      <LabelWithTooltip
                        labelText="Opening Payable Date"
                        tooltipText="This is the date for the opening payables"
                      />
                      <Flatpickr
                        value={contact.payableDate}
                        onChange={([date]) => setContact((prevItem: any) => ({ ...prevItem, payableDate: date }))}
                        className='form-control'
                        placeholder={'Opening Balance Date'.toTranslatedDisplay()}
                      />
                    </div>
                  }
                </div>
              </div>
            </div>
            <div className='tab-pane fade mb-5' id='kt_tab_pane_4' role='tabpanel'>
              <div className='row mb-7'>
                {
                  fieldConfig.filter(fc => fc.isVisible && !fc.isDeleted).map((fieldDetail, index) => (
                    <div className='col-md-3'>
                      <div className='mb-3'>
                        <div key={index} className="form-group">
                          <label className='form-label' htmlFor={fieldDetail.fieldName}>{fieldDetail.fieldHeading || fieldDetail.fieldName}</label>
                          <input
                            type={fieldDetail.fieldType || 'text'}
                            id={fieldDetail.fieldName}
                            name={fieldDetail.fieldName}
                            value={contact.customFields?.find(field => field.fieldName === fieldDetail.fieldName)?.fieldValue || ''}
                            className="form-control"
                            onChange={handleCustomFields}
                          />

                        </div>


                      </div>

                    </div>

                  ))
                }

              </div>
              <a href="#" onClick={handleAddFieldModalToggle} className="btn btn-outline btn-outline-dashed btn-outline-primary btn-active-light-primary">{'Add Custom Fields'.toTranslatedDisplay()}</a>
              {fieldConfig.length > 0 &&
                <a href="#" onClick={handleEditFieldModalToggle} className=" ms-2 btn btn-outline btn-outline-dashed btn-outline-secondary btn-active-light-secondary">{'Modify Custom Fields'.toTranslatedDisplay()}</a>
              }
            </div>
            <ButtonWithLoader shortcutTip='Ctrl+S / ⌘+S' shortcutKeys={
              ['ctrl+s', 'meta+s']
            } isLoading={isLoading} onClick={handleSubmit} >
              {'Save'.toTranslatedDisplay()}
            </ButtonWithLoader>
            <button type='button' className='btn btn-secondary ms-2' onClick={handleExit} tabIndex={3}>{'Cancel'.toTranslatedDisplay()}</button>
          </div>
        </div>
      </div>
      <CustomPrompt
        isVisible={isPromptVisible}
        onClose={handleClosePrompt}
        onConfirm={handleConfirmExit}
        message={'You have unsaved changes. Do you really want to leave?'.toTranslatedDisplay()}
        title={'Unsaved Changes'.toTranslatedDisplay()}
        PrimaryText={'OK'.toTranslatedDisplay()}
        secondaryText={'Cancel'.toTranslatedDisplay()}
      />
      <CustomModal
        title={'Add Custom Field'.toTranslatedDisplay()}
        isVisible={showAddFieldModal}
        onClose={handleAddFieldModalClose}
        body={<AddFieldConfigurator
          key={showAddFieldModal ? 'open' : 'closed'}
          objectName={'Contact'}
          onSave={function (fieldConfigs: FieldConfig[], save: boolean): void {}}
          onClose={function (fieldConfigUpdate?: boolean | undefined): void {
            handleAddFieldModalClose()
          }}
          pushFieldConfog={(configs) => setFieldConfigs(configs)}
        />}
        size="small"
        id="addCustomFieldModal"
      />
      <CustomModal
        title={'Modify Custom Field'.toTranslatedDisplay()}
        isVisible={showEditFieldModal}
        onClose={handleEditFieldModalClose}
        body={<EditFieldConfigurator objectName={'Contact'} onClose={handleEditFieldModalClose} onSave={function (fieldConfigs: FieldConfig[], save: boolean): void {
          throw new Error('Function not implemented.');
        }} />}
        size="small"
        id="addCustomFieldModal"
      />
      <CustomModal
        title={`${editingAddressIndex !== null ? 'Edit' : 'Add'} ${addressType === 'billing' ? 'Billing' : 'Shipping'} Address`}
        isVisible={showAddressModal}
        onClose={handleAddressModalClose}
        body={
          <div>
            <div className='mb-3'>
              <label className='form-label'>{'Address Name (Optional)'.toTranslatedDisplay()}</label>
              <input
                type='text'
                className='form-control'
                placeholder={'e.g., Warehouse, Office, etc.'.toTranslatedDisplay()}
                name='addressName'
                value={currentAddress.addressName || ''}
                onChange={handleAddressInputChange}
              />
            </div>
            <div className='mb-3'>
              <label className='form-label'>{'Address Detail'.toTranslatedDisplay()} <span className='text-danger'>*</span></label>
              <textarea
                className={`form-control ${addressDetailError ? 'is-invalid' : ''}`}
                placeholder={'Enter complete address'.toTranslatedDisplay()}
                name='addressDetail'
                value={currentAddress.addressDetail || ''}
                onChange={(e) => { setAddressDetailError(''); handleAddressInputChange(e); }}
                rows={4}
              />
              {addressDetailError && <div className='invalid-feedback d-block'>{addressDetailError}</div>}
            </div>
            <div className='d-flex justify-content-end gap-2'>
              <button type='button' className='btn btn-secondary' onClick={handleAddressModalClose}>
                {'Cancel'.toTranslatedDisplay()}
              </button>
              <button type='button' className='btn btn-primary' onClick={handleAddressSave}>
                {'Save Address'.toTranslatedDisplay()}
              </button>
            </div>
          </div>
        }
        size="small"
        id="addressModal"
      />
    </FormWrapper>
  );
}
